import {
  Box,
  Button,
  Checkbox,
  Grid,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { FormDialog } from "../form";
import { EmptyState } from "../feedback";
import { buildPhotoUrl } from "../../utils/photoUrl";

/**
 * Photo picker. Featured photos can be sourced from existing listing
 * photos, a file upload, or an online URL. Thumbnail photos are
 * always picked from listing photos (max 5).
 */
export default function PhotoPickerDialog({
  open,
  onClose,
  target,
  mode,
  setMode,
  listingSearch,
  setListingSearch,
  listingResults,
  listingLoading,
  listingNotice,
  listingSelection,
  toggleListingSelection,
  onApplyListings,
  photoFile,
  setPhotoFile,
  photoUrl,
  setPhotoUrl,
  photoUploading,
  onUploadFile,
  onImportUrl,
  editingId,
}) {
  const isFeatured = target === "featured";
  const title = isFeatured ? "Set featured photo" : "Pick thumbnails";

  const submit =
    mode === "listings"
      ? {
          onSubmit: onApplyListings,
          label: isFeatured ? "Use selected" : "Save thumbnails",
          disable: !listingSelection.length || listingLoading,
        }
      : mode === "browse"
        ? {
            onSubmit: onUploadFile,
            label: "Upload",
            disable: !photoFile || photoUploading,
          }
        : {
            onSubmit: onImportUrl,
            label: "Upload from URL",
            disable: !photoUrl?.trim() || photoUploading,
          };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={title}
      submitting={photoUploading || listingLoading}
      onSubmit={submit.onSubmit}
      submitLabel={submit.label}
      disableSubmit={submit.disable}
      maxWidth="sm"
    >
      {isFeatured ? (
        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v)}
          variant="fullWidth"
          sx={{
            mb: 2,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab value="listings" label="From listings" />
          <Tab value="browse" label="Upload file" />
          <Tab value="url" label="Online URL" />
        </Tabs>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick up to 5 photos from your menu listings. Selected{" "}
          {listingSelection.length} / 5.
        </Typography>
      )}

      {mode === "listings" && (
        <Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Search listings…"
            value={listingSearch}
            onChange={(event) => setListingSearch(event.target.value)}
            sx={{ mb: 2 }}
          />
          {listingNotice && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              {listingNotice}
            </Typography>
          )}
          {listingLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading listing photos…
            </Typography>
          ) : listingResults.length > 0 ? (
            <Grid container spacing={1}>
              {listingResults.map((listing) => {
                const photo = listing.featuredPhotoUrl;
                const isSelected = photo && listingSelection.includes(photo);
                return (
                  <Grid item xs={6} sm={4} md={3} key={listing.id}>
                    <Paper
                      variant="outlined"
                      onClick={() => toggleListingSelection(photo)}
                      sx={{
                        cursor: "pointer",
                        p: 0.5,
                        borderColor: isSelected ? "primary.main" : "divider",
                        borderWidth: isSelected ? 2 : 1,
                        position: "relative",
                        transition: "border-color 120ms ease",
                      }}
                    >
                      {isSelected && (
                        <Checkbox
                          checked
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(255,255,255,0.85)",
                            borderRadius: "50%",
                            p: 0.25,
                            zIndex: 1,
                          }}
                        />
                      )}
                      <Box
                        component="img"
                        src={buildPhotoUrl(photo)}
                        alt={listing.name}
                        sx={{
                          width: "100%",
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 1,
                          display: "block",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.75,
                          textAlign: "center",
                          color: "text.secondary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {listing.name}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <EmptyState
              title={editingId ? "No listing photos found" : "Save first"}
              description={
                editingId
                  ? "Add a listing with a featured photo, then come back here."
                  : "Save the vendor before browsing its listing photos."
              }
              dense
            />
          )}
        </Box>
      )}

      {isFeatured && mode === "browse" && (
        <Box>
          <Button variant="outlined" component="label">
            {photoFile ? "Choose a different file" : "Choose file"}
            <input
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(event) => setPhotoFile(event.target.files?.[0] || null)}
            />
          </Button>
          {photoFile && (
            <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
              {photoFile.name}
            </Typography>
          )}
        </Box>
      )}

      {isFeatured && mode === "url" && (
        <TextField
          fullWidth
          size="small"
          label="Image URL"
          placeholder="https://example.com/photo.jpg"
          value={photoUrl}
          onChange={(event) => setPhotoUrl(event.target.value)}
        />
      )}
    </FormDialog>
  );
}
